import React, { useState, useEffect } from 'react';
import {
  Layout,
  Menu,
  Breadcrumb,
  Card,
  Table,
  Button,
  Space,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Statistic,
  Row,
  Col,
  Popconfirm,
  Switch,
  Tooltip,
  Progress
} from 'antd';
import {
  UserOutlined,
  DashboardOutlined,
  LogoutOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
  SafetyOutlined,
  MobileOutlined,
  BarChartOutlined,
  ControlOutlined,
  DesktopOutlined,
  SettingOutlined,
  FileTextOutlined,
  KeyOutlined,
  VideoCameraOutlined,
  AuditOutlined,
  ClusterOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authService } from '../services/auth';
import { adminService, UserWithDevices, User } from '../services/admin';
// New License Management Components
import LicenseList from './LicenseList';
import LicenseEdit from './LicenseEdit';
// New License Configuration Components
import { LicenseConfigOverview } from '../components';
import LicenseConfigModal from '../components/LicenseConfigModal';
// New Module Components - LicensedDevice, LicenseUsage, Content
import LicensedDeviceList from './LicensedDeviceList';
import LicenseUsageList from './LicenseUsageList';
import ContentList from './ContentList';

const { Header, Content, Sider } = Layout;
const { Option } = Select;

const AdminDashboard: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('dashboard');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [licenseConfigModalVisible, setLicenseConfigModalVisible] = useState(false);
  const [selectedLicenseConfigUser, setSelectedLicenseConfigUser] = useState<User | null>(null);
  const [currentLicenseConfig, setCurrentLicenseConfig] = useState<any>(null);
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [isLicenseEditMode, setIsLicenseEditMode] = useState<'create' | 'edit' | null>(null);
  const [editingLicenseId, setEditingLicenseId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  // Fetch user statistics
  const { data: stats, refetch: refetchStats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: adminService.getStats,
  });

  // Fetch users - simplified version (no device info)
  const { data: users, refetch: refetchUsers } = useQuery({
    queryKey: ['users'],
    queryFn: adminService.getUsers,
  });

  // Fetch device statistics
  const { data: deviceStats } = useQuery({
    queryKey: ['device-stats'],
    queryFn: adminService.getDeviceStats,
  });

  // Fetch license configuration statistics
  const { data: licenseConfigs = [] } = useQuery({
    queryKey: ['license-configs'],
    queryFn: adminService.getAllLicenseConfigs,
  });

  useEffect(() => {
    document.title = '管理面板 - 用户管理系统';
  }, []);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const showCreateModal = () => {
    setEditingUser(null);
    form.setFieldsValue({
      username: '',
      email: '',
      password: '',
      role: 'user',
      status: 'active',
    });
    setIsModalVisible(true);
  };

  const showEditModal = (user: User) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      role: user.role,
      status: user.status,
    });
    setIsModalVisible(true);
  };

  // License Configuration Modal Functions
  const showLicenseConfigModal = async (user: User) => {
    setSelectedLicenseConfigUser(user);
    setLicenseLoading(true);

    try {
      const config = await adminService.getUserLicenseConfig(user.id);
      setCurrentLicenseConfig(config);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setCurrentLicenseConfig(null);
      } else {
        message.error('加载许可证配置失败: ' + (error.response?.data?.message || error.message));
      }
    } finally {
      setLicenseLoading(false);
    }
    setLicenseConfigModalVisible(true);
  };

  const handleLicenseConfigSave = async (data: any) => {
    if (!selectedLicenseConfigUser) return;

    try {
      await adminService.updateUserLicenseConfig(selectedLicenseConfigUser.id, data);
      message.success('许可证配置保存成功');

      // Reload the current config
      const updatedConfig = await adminService.getUserLicenseConfig(selectedLicenseConfigUser.id);
      setCurrentLicenseConfig(updatedConfig);
    } catch (error: any) {
      console.error('Save failed:', error);
      message.error(error.response?.data?.message || '保存许可证配置失败');
      throw error; // Let modal handle the error
    }
  };

  const handleLicenseConfigClose = () => {
    setLicenseConfigModalVisible(false);
    setSelectedLicenseConfigUser(null);
    setCurrentLicenseConfig(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingUser) {
        await adminService.updateUser(editingUser.id, values);
        message.success('用户更新成功');
      } else {
        await adminService.createUser(values);
        message.success('用户创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      refetchUsers();
      refetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleDeleteUser = async (userId: number) => {
    try {
      await adminService.deleteUser(userId);
      message.success('用户删除成功');
      refetchUsers();
      refetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleStatusChange = async (userId: number, status: string) => {
    try {
      await adminService.updateUserStatus(userId, status);
      message.success(`用户已${status === 'active' ? '激活' : '停用'}`);
      refetchUsers();
      refetchStats();
    } catch (error: any) {
      message.error(error.response?.data?.message || '状态更新失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>
          {role === 'admin' ? '管理员' : '用户'}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string, record: User) => (
        <Switch
          checked={status === 'active'}
          onChange={(checked) => handleStatusChange(record.id, checked ? 'active' : 'inactive')}
        />
      ),
      width: 80,
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('zh-CN'),
      width: 150,
    },
    {
      title: '操作',
      key: 'actions',
      render: (text: any, record: User) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
          >
            编辑
          </Button>
          <Button
            size="small"
            icon={<SettingOutlined />}
            onClick={() => showLicenseConfigModal(record)}
            style={{ borderColor: '#1890ff', color: '#1890ff' }}
          >
            许可配置
          </Button>
          <Popconfirm
            title="确定要删除这个用户吗？"
            onConfirm={() => handleDeleteUser(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const menuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: '仪表板',
    },
    {
      key: 'users',
      icon: <UserOutlined />,
      label: '用户管理',
    },
    {
      key: 'licenses',
      icon: <FileTextOutlined />,
      label: '许可证管理',
      children: [
        {
          key: 'license-list',
          icon: <BarChartOutlined />,
          label: '许可证列表',
        },
        {
          key: 'license-summary',
          icon: <SafetyOutlined />,
          label: '许可证配置',
        }
      ]
    },
    {
      key: 'management',
      icon: <ClusterOutlined />,
      label: '系统管理',
      children: [
        {
          key: 'licensed-devices',
          icon: <MobileOutlined />,
          label: '授权设备管理',
        },
        {
          key: 'license-usage',
          icon: <AuditOutlined />,
          label: '使用分析统计',
        },
        {
          key: 'content-manager',
          icon: <VideoCameraOutlined />,
          label: '内容库管理',
        }
      ]
    }
  ];

  const renderContent = () => {
    switch (selectedMenu) {
      case 'dashboard':
        return (
          <div>
            <Row gutter={16}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="总用户数"
                    value={stats?.totalUsers || 0}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="激活用户"
                    value={stats?.activeUsers || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="禁用用户"
                    value={stats?.disabledUsers || 0}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="总设备数"
                    value={deviceStats?.totalDevices || 0}
                    prefix={<MobileOutlined />}
                  />
                </Card>
              </Col>
            </Row>
            <div style={{ marginTop: 24 }}>
              <Card title="系统概览">
                <p>欢迎使用管理面板。请使用左侧菜单导航到不同的管理功能。</p>
                <ul>
                  <li>用户管理 - 管理用户账户和权限</li>
                  <li>许可证管理 - 创建和管理许可证</li>
                  <li>系统管理 - 查看设备和使用统计</li>
                </ul>
              </Card>
            </div>
          </div>
        );
      case 'users':
        return (
          <div>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="总用户数"
                    value={stats?.totalUsers || 0}
                    prefix={<TeamOutlined />}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="激活用户"
                    value={stats?.activeUsers || 0}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="禁用用户"
                    value={stats?.disabledUsers || 0}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card>
                  <Statistic
                    title="管理员用户"
                    value={stats?.adminUsers || 0}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col span={24} style={{ textAlign: 'right' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={showCreateModal}
                  style={{ marginBottom: 16 }}
                >
                  新建用户
                </Button>
              </Col>
            </Row>
            <div className="table-container">
              <Table
                dataSource={users}
                columns={columns}
                rowKey="id"
                loading={!users}
                pagination={{
                  defaultPageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total) => `共 ${total} 条`,
                }}
                expandable={{
                  expandedRowRender: (record) => (
                    <Card size="small" title="用户信息" style={{ margin: 0 }}>
                      <Row gutter={24}>
                        <Col span={12}>
                          <div style={{ padding: 8 }}>
                            <h4>用户统计：</h4>
                            <p>用户ID：{record.id}</p>
                            <p>状态：{record.status === 'active' ? <Tag color="green">活跃</Tag> : <Tag color="red">禁用</Tag>}</p>
                            <p>角色：{record.role === 'admin' ? <Tag color="red">管理员</Tag> : <Tag color="blue">用户</Tag>}</p>
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={{ padding: 8 }}>
                            <h4>联系信息：</h4>
                            <p>用户名：{record.username}</p>
                            <p>邮箱：{record.email}</p>
                            <p>注册时间：{new Date(record.createdAt).toLocaleString('zh-CN')}</p>
                          </div>
                        </Col>
                      </Row>
                    </Card>
                  ),
                  rowExpandable: (record) => true,
                }}
              />
            </div>
          </div>
        );
      case 'license-summary':
        return (
          <div>
            <Card title="许可证配置概述" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic
                    title="用户总数"
                    value={users?.length || 0}
                    prefix={<SafetyOutlined />}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="激活配置"
                    value={licenseConfigs.filter(config => config.isActive).length}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="未配置用户"
                    value={(users?.length || 0) - licenseConfigs.length}
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Col>
                <Col span={6}>
                  <Statistic
                    title="平均设备限制"
                    value={licenseConfigs.length > 0 ? Math.round(licenseConfigs.reduce((sum, config) => sum + config.maxDevices, 0) / licenseConfigs.length) : 0}
                    prefix={<DesktopOutlined />}
                  />
                </Col>
              </Row>
            </Card>
            <LicenseConfigOverview />
          </div>
        );
      case 'licensed-devices':
        return <LicensedDeviceList />;
      case 'license-usage':
        return <LicenseUsageList />;
      case 'content-manager':
        return <ContentList />;
      case 'license-list':
        return (
          <LicenseList
            onCreateLicense={() => setSelectedMenu('license-new')}
            onEditLicense={(licenseId) => {
              setEditingLicenseId(licenseId);
              setSelectedMenu('license-edit');
            }}
          />
        );
      case 'license-new':
        return (
          <LicenseEdit
            mode="create"
            onSuccess={() => {
              setSelectedMenu('license-list');
              message.success('许可证创建成功');
            }}
            onCancel={() => setSelectedMenu('license-list')}
          />
        );
      case 'license-edit':
        return (
          <LicenseEdit
            mode="edit"
            licenseId={editingLicenseId!}
            onSuccess={() => {
              setSelectedMenu('license-list');
              message.success('许可证更新成功');
            }}
            onCancel={() => setSelectedMenu('license-list')}
          />
        );
      default:
        return null;
    }
  };

  // const handleLicenseConfigSave = async (data: any) => {
  //   if (!selectedLicenseConfigUser) return;

  //   try {
  //     await adminService.updateUserLicenseConfig(selectedLicenseConfigUser.id, data);
  //     message.success('许可证配置保存成功');

  //     // Reload the current config
  //     const updatedConfig = await adminService.getUserLicenseConfig(selectedLicenseConfigUser.id);
  //     setCurrentLicenseConfig(updatedConfig);
  //   } catch (error: any) {
  //     console.error('Save failed:', error);
  //     message.error(error.response?.data?.message || '保存许可证配置失败');
  //     throw error; // Let modal handle the error
  //   }
  // };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        theme="dark"
      >
        <div style={{ height: 32, margin: 16, background: 'rgba(255, 255, 255, 0.2)' }} />
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedMenu]}
          items={menuItems}
          onClick={({ key }) => setSelectedMenu(key)}
        />
      </Sider>

      <Layout>
        <Header className="site-layout-background" style={{ padding: '0 16px', background: '#fff' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '100%' }}>
            <Breadcrumb>
              <Breadcrumb.Item>管理面板</Breadcrumb.Item>
              <Breadcrumb.Item>
                {selectedMenu === 'dashboard' && '仪表板'}
                {selectedMenu === 'users' && '用户管理'}
                {selectedMenu === 'license-list' && '许可证列表'}
                {selectedMenu === 'license-new' && '新建许可证'}
                {selectedMenu === 'license-edit' && '编辑许可证'}
                {selectedMenu === 'license-summary' && '许可证配置'}
                {selectedMenu === 'licensed-devices' && '授权设备管理'}
                {selectedMenu === 'license-usage' && '使用分析统计'}
                {selectedMenu === 'content-manager' && '内容库管理'}
              </Breadcrumb.Item>
            </Breadcrumb>
            <Button
              type="primary"
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              退出
            </Button>
          </div>
        </Header>

        <Content style={{ margin: '16px', background: '#fff', padding: 24, minHeight: 280 }}>
          {renderContent()}
        </Content>
      </Layout>

      {/* Create/Edit User Modal */}
      <Modal
        title={editingUser ? '编辑用户' : '创建用户'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ role: 'user', status: 'active' }}
        >
          <Form.Item
            label="用户名"
            name="username"
            rules={[{ required: true, message: '请输入用户名' }]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            label="邮箱"
            name="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              label="密码"
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="请输入密码" />
            </Form.Item>
          )}

          <Form.Item
            label="角色"
            name="role"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Option value="user">用户</Option>
              <Option value="admin">管理员</Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="状态"
            name="status"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="active">活跃</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* License Configuration Modal */}
      <LicenseConfigModal
        visible={licenseConfigModalVisible}
        userId={selectedLicenseConfigUser?.id || 0}
        config={currentLicenseConfig}
        onSave={handleLicenseConfigSave}
        onCancel={handleLicenseConfigClose}
        currentConfig={currentLicenseConfig}
        loading={licenseLoading}
      />
    </Layout>
  );
};

export default AdminDashboard;